import { useMemo } from 'react';
import { parseData, useIndicatorConfigs } from './indicator-configs.hook';
import { TradingFormStrategy } from '../../trading-form/trading-form.types';
import { Time } from 'lightweight-charts';
import {
  DataFeedResponse,
  Timeframe
} from '@server/data-feed/schemas/output.schema';
import { TradingChartData } from '../components/chart.context';
import { useObservable } from '@client/foundation/shared/utils/observable.hook';
import { useChartStreamContext } from '../components/chart-stream.context';

export type EntryTrigger = {
  time: Time;
};

const extractSignalTypesToPlot = (
  strategy: TradingFormStrategy | undefined
) => {
  const byType = new Set<string>();
  const signalBuckets = [
    {
      direction: 'long' as const,
      bucket: 'entries' as const,
      items: strategy?.value?.long?.entries || []
    },
    {
      direction: 'long' as const,
      bucket: 'exits' as const,
      items: strategy?.value?.long?.exits || []
    },
    {
      direction: 'short' as const,
      bucket: 'entries' as const,
      items: strategy?.value?.short?.entries || []
    },
    {
      direction: 'short' as const,
      bucket: 'exits' as const,
      items: strategy?.value?.short?.exits || []
    }
  ];

  signalBuckets.forEach(({ items }) => {
    items.forEach((condition) => {
      if (condition.enabled === false) {
        return;
      }

      if (condition.plot) {
        byType.add(condition.value);
      }
    });
  });

  return byType;
};

export const useChartData = (): TradingChartData => {
  const { formState, timeframeIndex, feedResponse$ } =
    useChartStreamContext();
  const data = useObservable(() => feedResponse$, undefined);
  const timeframeFormState = formState.timeframes?.[timeframeIndex];
  const indicatorConfigs = useIndicatorConfigs(
    data,
    timeframeIndex,
    timeframeFormState?.indicators
  );
  const timeframeFeed = data?.timeframes?.[timeframeIndex];

  return useMemo(() => {
    const trades = data?.trades ?? [];

    const entries: DataFeedResponse['trades'] = parseData(
      trades.filter(({ type }) => type === 'ENTRY'),
      (item, time) => ({
        ...item,
        time
      })
    );

    const adds: DataFeedResponse['trades'] = parseData(
      trades.filter(({ type }) => type === 'ADD'),
      (item, time) => ({
        ...item,
        time
      })
    );

    const exits: DataFeedResponse['trades'] = parseData(
      trades.filter(({ type }) => type === 'EXIT'),
      (item, time) => ({
        ...item,
        time
      })
    );

    const stopLosses: DataFeedResponse['trades'] = parseData(
      trades.filter(({ type }) => type === 'STOP_LOSS'),
      (item, time) => ({
        ...item,
        time
      })
    );

    const takeProfits: DataFeedResponse['trades'] = parseData(
      trades.filter(({ type }) => type === 'TAKE_PROFIT'),
      (item, time) => ({
        ...item,
        time
      })
    );

    const toEntryTriggers = (
      triggers: { time: string; value: boolean }[] | undefined
    ): EntryTrigger[] => {
      if (!triggers) return [];

      return parseData(
        triggers.filter(({ value }) => value),
        (_, time) => ({ time })
      );
    };

    const longEntryTriggers = toEntryTriggers(
      timeframeFeed?.tradeTriggers?.long?.entryTriggers
    );
    const shortEntryTriggers = toEntryTriggers(
      timeframeFeed?.tradeTriggers?.short?.entryTriggers
    );
    const longEntryTriggersConfluence = toEntryTriggers(
      timeframeFeed?.tradeTriggersConfluence?.long?.entryTriggers
    );
    const shortEntryTriggersConfluence = toEntryTriggers(
      timeframeFeed?.tradeTriggersConfluence?.short?.entryTriggers
    );

    const signalTypesToPlot = extractSignalTypesToPlot(
      timeframeFormState?.strategy
    );

    const signalData: Timeframe['signals'] =
      timeframeFeed?.signals
        ?.filter(({ type }) => signalTypesToPlot.has(type))
        .map((signal) => {
          const parsedData = parseData(
            signal.data.filter(({ value }) => value),
            (item, time) => ({
              time,
              value: item.value
            })
          );

          return {
            ...signal,
            data: parsedData
          };
        }) || [];

    return {
      analysis: {
        signals: {
          raw: signalData,
          triggers: {
            long: longEntryTriggers,
            short: shortEntryTriggers
          },
          triggersConfluence: {
            long: longEntryTriggersConfluence,
            short: shortEntryTriggersConfluence
          }
        }
      },
      execution: {
        entries,
        adds,
        exits,
        stopLosses,
        takeProfits
      },
      indicatorConfigs
    };
  }, [
    data,
    indicatorConfigs,
    timeframeFeed,
    timeframeFormState?.strategy
  ]);
};
